import type { ReactNode } from "react";
import { Task, TaskGroupBy, TaskStatus } from "@common/types/task";
import type { BoardColumnDef } from "@design-system/Board/Board.types";
import {
    getAllPossibleGroupKeys,
    getGroupTitleForKey,
    getTaskGroupId,
    shouldHideEmptyTaskGroups,
} from "../taskListUtils";
import type { TaskWorkspaceData } from "../useTaskWorkspaceData";

/** The lookups that name List groups, and so Board columns. */
export type TaskBoardColumnLookups = Pick<
    TaskWorkspaceData,
    | "workspaceMemberIds"
    | "workspaceMembersMap"
    | "workspaceTaskGroupIds"
    | "workspaceTaskGroupsMap"
    | "closeStageIds"
    | "closeStagesMap"
    | "closeIds"
    | "closesMap"
> & { clientNamesById: ReadonlyMap<string, string> };

export interface TaskBoardColumnsArgs {
    tasks: readonly Task[];
    groupBy: TaskGroupBy;
    statusFilter: TaskStatus[] | undefined;
    lookups: TaskBoardColumnLookups;
    isEmptyColumnsShown: boolean;
    getIcon?: (args: { groupBy: TaskGroupBy; groupKey: string }) => ReactNode;
}

const countTasksByGroupKey = (tasks: readonly Task[], groupBy: TaskGroupBy) => {
    const counts = new Map<string, number>();
    tasks.forEach((task) => {
        const groupKey = getTaskGroupId(groupBy, task);
        counts.set(groupKey, (counts.get(groupKey) ?? 0) + 1);
    });
    return counts;
};

/**
 * The board's columns for its column grouping: the same ids, titles and
 * order as List's groups, empty ones dropped where List drops them or when
 * `isEmptyColumnsShown` is off.
 */
export const buildTaskBoardColumns = ({
    tasks,
    groupBy,
    statusFilter,
    lookups,
    isEmptyColumnsShown,
    getIcon,
}: TaskBoardColumnsArgs): BoardColumnDef[] => {
    const groupKeys = getAllPossibleGroupKeys({
        groupBy,
        statusFilter,
        workspaceMemberIds: lookups.workspaceMemberIds,
        taskGroupIds: lookups.workspaceTaskGroupIds,
        closeStageIds: lookups.closeStageIds,
        closeIds: lookups.closeIds,
        clientsMap: lookups.clientNamesById,
    });
    const taskCounts = countTasksByGroupKey(tasks, groupBy);
    const isEmptyHidden =
        !isEmptyColumnsShown || shouldHideEmptyTaskGroups(groupBy);

    return groupKeys
        .filter((groupKey) => !isEmptyHidden || taskCounts.has(groupKey))
        .map((groupKey) => ({
            id: groupKey,
            title: getGroupTitleForKey({
                groupBy,
                groupKey,
                workspaceMembersMap: lookups.workspaceMembersMap,
                taskGroupsMap: lookups.workspaceTaskGroupsMap,
                closeStagesMap: lookups.closeStagesMap,
                closesMap: lookups.closesMap,
                clientsMap: lookups.clientNamesById,
            }),
            icon: getIcon?.({ groupBy, groupKey }),
        }));
};
